import { createRouter, createWebHashHistory } from "vue-router";

import NProgress from "nprogress/nprogress.js";

import { ROLE_HOME, canOpen } from "@/data/menus";
import { ENTITY_SCHEMAS } from "@/data/entitySchemas";
import { roleComponent } from "@/services/roleComponent";

// Layouts
import LayoutSimple from "@/layouts/variations/Simple.vue";
import AppLayout from "@/layouts/variations/Backend.vue"; // общий каркас для всех ролей; меню зависит от роли

// Auth
const AuthSignIn = () => import("@/views/auth/SignInView.vue");
const AuthSignUp = () => import("@/views/auth/SignUpView.vue");

// Errors
const Error400 = () => import("@/views/errors/400View.vue");
const Error401 = () => import("@/views/errors/401View.vue");
const Error403 = () => import("@/views/errors/403View.vue");
const Error404 = () => import("@/views/errors/404View.vue");
const Error500 = () => import("@/views/errors/500View.vue");
const Error503 = () => import("@/views/errors/503View.vue");

// Общие
const Dashboard = () => import("@/views/backend/DashboardView.vue");
const TableView = () => import("@/views/admin/TableView.vue"); // универсальный список: GET /api{путь}
const VehiclesView = () => import("@/views/admin/VehiclesView.vue");
const EntityCreateView = () => import("@/views/admin/EntityCreateView.vue"); // форма создания: поля из data/entitySchemas.js
const VehicleCalendarView = () => import("@/views/admin/VehicleCalendarView.vue");
const HealthView = () => import("@/views/admin/HealthView.vue");
const StatusView = () => import("@/views/admin/StatusView.vue");
const RevenueByTourTypeView = () => import("@/views/admin/analytics/RevenueByTourTypeView.vue");
const VehicleUtilizationView = () => import("@/views/admin/analytics/VehicleUtilizationView.vue");
const GuideOrdersView = () => import("@/views/admin/analytics/GuideOrdersView.vue");

// Клиент
const CatalogView = () => import("@/views/client/CatalogView.vue");
const CartView = () => import("@/views/client/CartView.vue");
const ClientHomeView = () => import("@/views/client/HomeView.vue");
const TourView = () => import("@/views/client/TourView.vue");

// Карточки, общие для ролей (заказ с оплатой QR и участниками, обращение с ответом); у предложения экран зависит от роли
const OrderDetailView = () => import("@/views/orders/OrderDetailView.vue");
const SupportTicketView = () => import("@/views/support/SupportTicketView.vue");
const PersonalOfferPage = roleComponent(
  { admin: () => import("@/views/admin/EditPersonalOfferView.vue") },
  () => import("@/views/client/PersonalOfferView.vue"),
);

// Транспортник
const CarsView = () => import("@/views/transporter/CarsView.vue");
const TransporterCompanyView = () => import("@/views/transporter/TransporterView.vue");
const TransporterFinanceView = () => import("@/views/transporter/FinanceView.vue");

const TourTemplateEdit = () => import("@/views/admin/TourTemplateEdit.vue"); // тур: и создание (в т.ч. по шаблону), и правка
const CompanySettingsView = () => import("@/views/admin/CompanySettingsView.vue");

// «Добавить» для каждой сущности из схем: /orders/create, … (у сущности со своей страницей создания, schema.page, — своя страница)
const createRoutes = Object.keys(ENTITY_SCHEMAS).filter((entity) => !ENTITY_SCHEMAS[entity].page).map((entity) => ({
  path: `${entity}/create`,
  name: `create-${entity}`,
  component: EntityCreateView,
  props: { entity },
}));

const routes = [
  {
    path: "/",
    redirect: () => {
      const home = ROLE_HOME[localStorage.getItem("userrole")];

      return localStorage.getItem("isAuth") && home ? home : "/auth/signin";
    },
  },

  // ==================== ПРИЛОЖЕНИЕ (все роли; доступ ограничивает beforeEach) ====================
  {
    path: "/",
    component: AppLayout,
    children: [
      { path: "Adashboard", name: "landing", component: Dashboard },

      ...createRoutes,

      // Клиент: каталог с добавлением в корзину и сама корзина
      { path: "home", name: "ClientHome", component: ClientHomeView },
      { path: "catalog", name: "Catalog", component: CatalogView },
      { path: "catalog/:slug", name: "Tour", component: TourView },
      { path: "cart", name: "Cart", component: CartView },

      // Пользователи и профили
      { path: "control", component: TableView },
      { path: "control/:id", name: "EditView", component: () => import("@/views/admin/EditUserView.vue") },
      { path: "client-profiles", name: "AdminClients", component: TableView },
      { path: "client-profiles/:id", name: "AdminEditClient", component: () => import("@/views/admin/ClientEdit.vue") },
      { path: "guide-profiles", name: "guide-profiles", component: TableView },
      { path: "guide-profiles/:id", name: "AdminEditGuide", component: () => import("@/views/admin/GuideEdit.vue") },
      { path: "insurer-profiles", component: TableView },
      { path: "insurer-profiles/:id", name: "InsurerEditClient", component: () => import("@/views/admin/EditInsurerView.vue") },
      { path: "documents", component: TableView },
      { path: "documents/:id", name: "AdminEditDocument", component: () => import("@/views/admin/EditDocumentView.vue") },

      // Туры и доп. услуги
      { path: "tour-templates", component: TableView },
      { path: "tour-templates/create", name: "CreateTourTemplate", component: TourTemplateEdit },
      { path: "tour-templates/:id", name: "EditTourTemplate", component: TourTemplateEdit },
      { path: "extra-services", component: TableView },
      { path: "extra-services/:id", name: "EditExtraService", component: () => import("@/views/admin/ExtraServiceEdit.vue") },

      // Транспорт
      { path: "vehicle-calendar", name: "VehicleCalendar", component: VehicleCalendarView },
      { path: "vehicles", component: VehiclesView },
      { path: "vehicles/:id", name: "EditVehicle", component: () => import("@/views/admin/VehicleEdit.vue") },
      { path: "vehicle-types", component: TableView },
      { path: "vehicle-types/:id", name: "EditVehicleType", component: () => import("@/views/admin/VehicleTypeEdit.vue") },
      { path: "vehicle-bookings", component: TableView },
      { path: "vehicle-bookings/:id", name: "EditVehicleBooking", component: () => import("@/views/admin/EditVehicleBookingView.vue") },

      // Транспортник
      { path: "mycars", component: CarsView },
      { path: "cash", component: TransporterFinanceView },
      { path: "transport-company/my", component: TransporterCompanyView },

      // Заказы, участники, корзины, платежи
      { path: "orders", component: TableView },
      { path: "orders/:id", name: "OrderDetail", component: OrderDetailView },
      { path: "orders/:id/edit", name: "EditOrder", component: () => import("@/views/admin/OrderEdit.vue") },
      { path: "order-items", component: TableView },
      { path: "tour-participants", component: TableView },
      { path: "carts", component: TableView },
      { path: "carts/:id", name: "EditCart", component: () => import("@/views/admin/EditCartView.vue") },
      { path: "payments", component: TableView },
      { path: "payments/:id", name: "EditPayment", component: () => import("@/views/admin/EditPaymentView.vue") },

      // Предложения, страхование, поддержка, уведомления
      { path: "personal-offers", component: TableView },
      { path: "personal-offers/:id", name: "PersonalOffer", component: PersonalOfferPage },
      { path: "insurance-requests", component: TableView },
      { path: "insurance-requests/:id", name: "EditInsuranceRequest", component: () => import("@/views/admin/InsuranceRequestEdit.vue") },
      { path: "support-tickets", component: TableView },
      { path: "support-tickets/:id", name: "SupportTicket", component: SupportTicketView },
      { path: "notifications", component: TableView },

      // Аналитика и отчёты (админ)
      { path: "analytics/revenue-by-tour-type", name: "RevenueByTourType", component: RevenueByTourTypeView },
      { path: "analytics/vehicle-utilization", name: "VehicleUtilization", component: VehicleUtilizationView },
      { path: "analytics/guide-orders", name: "GuideOrders", component: GuideOrdersView },
      { path: "analytics/client-statistics", name: "ClientStatistics", component: TableView },
      { path: "analytics/guide-performance", name: "GuidePerformance", component: TableView },
      { path: "analytics/booking-trends", name: "BookingTrends", component: TableView },
      { path: "analytics/revenue-over-time", name: "RevenueOverTime", component: TableView },
      { path: "reports/sales", name: "SalesReport", component: TableView },
      { path: "reports/clients", name: "ClientsReport", component: TableView },
      { path: "reports/vehicles", name: "VehiclesReport", component: TableView },
      { path: "reports/guides", name: "GuidesReport", component: TableView },

      // Система (админ)
      { path: "health", component: HealthView },
      { path: "status", component: StatusView },
      { path: "company-settings", name: "CompanySettings", component: CompanySettingsView },
    ],
  },

  // ==================== ВХОД И РЕГИСТРАЦИЯ ====================
  {
    path: "/auth",
    component: LayoutSimple,
    children: [
      { path: "signin", name: "auth-signin", component: AuthSignIn },
      { path: "client", name: "client-signup", component: AuthSignUp },
    ],
  },

  // ==================== ОШИБКИ ====================
  {
    path: "/errors",
    component: LayoutSimple,
    children: [
      { path: "400", name: "error-400", component: Error400 },
      { path: "401", name: "error-401", component: Error401 },
      { path: "403", name: "error-403", component: Error403 },
      { path: "404", name: "error-404", component: Error404 },
      { path: "500", name: "error-500", component: Error500 },
      { path: "503", name: "error-503", component: Error503 },
    ],
  },

  { path: "/:pathMatch(.*)*", redirect: "/errors/404" },
];

const router = createRouter({
  history: createWebHashHistory(),
  linkActiveClass: "active",
  linkExactActiveClass: "",
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
  routes,
});

// Доступ: без входа — на страницу входа; вошедшего пускаем только в разделы его роли (данные всё равно фильтрует API)
router.beforeEach((to) => {
  if (to.path.startsWith("/auth") || to.path.startsWith("/errors")) {
    return true;
  }

  const role = localStorage.getItem("userrole");
  if (!localStorage.getItem("isAuth") || !localStorage.getItem("token") || !ROLE_HOME[role]) {
    return "/auth/signin";
  }

  // «Корзина» клиента — отдельный экран /cart (со строкой «Оформить заказ»), а не таблица /carts
  if (role === "client" && to.path === "/carts") return "/cart";

  return canOpen(role, to.path) ? true : ROLE_HOME[role];
});

// NProgress
NProgress.configure({ showSpinner: false });

router.beforeResolve((to, from, next) => {
  if (to.name) {
    NProgress.start();
  }
  next();
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
